/** The troop rules as pure functions (DESIGN §7): spawn cadence, stat growth, tier bonus and the formation (sim/troops.ts uses them). */
import { TroopsData } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import type { Troop } from "./entities";

/** Ticks between troop spawns at a given base level: linear from the L1 anchor to the max-level anchor (DESIGN §7). */
export function troopSpawnIntervalTicks(rules: TroopsData, baseLevel: number, maxLevel: number): number {
  const span = Math.max(1, maxLevel - 1);
  const l = Math.min(Math.max(baseLevel, 1), maxLevel) - 1;
  return rules.spawnIntervalTicksAtL1 - Math.floor(((rules.spawnIntervalTicksAtL1 - rules.spawnIntervalTicksAtMaxLevel) * l) / span);
}

/** Stat multiplier at a base level: growth^(level−1), computed by repeated fixed-point multiplication so it is exact. */
export function troopStatMult(rules: TroopsData, baseLevel: number): Fix64 {
  let m = Fix64.One;
  for (let i = 1; i < baseLevel; i++) m = m.mul(rules.statGrowth);
  return m;
}

/**
 * The troops' stat multiplier with a GearBonus troop bonus (VIP troop strength): statMult × (1 + bonus). No bonus
 * (absent or zero) returns `statMult` itself, so a run without it is bit-for-bit the run it always was.
 */
export function troopStatMultWithBonus(statMult: Fix64, bonus: Fix64 | undefined): Fix64 {
  return bonus === undefined || bonus.isZero() ? statMult : statMult.mul(Fix64.One.add(bonus));
}

/**
 * The multiplier the fortress's in-run tier gives its troops' health and damage (DESIGN §7): 1 + baseTierBonus per
 * tier above the first, so an Upgrade card on the fortress makes its soldiers visibly tougher, not just its strike.
 */
export function troopTierMult(rules: TroopsData, tier: number): Fix64 {
  return Fix64.One.add(rules.baseTierBonus.mulInt(Math.max(0, tier - 1)));
}

/**
 * Rally Bark (Pets v2, sim/petBark.ts) as the troops see it while it lasts: fortress troops within `reach2` (squared
 * cells) of the flag's cell deal `damageMult` × their damage and take `takenMult` × a blocked raider's blow.
 */
export interface TroopBark { readonly damageMult: Fix64; readonly takenMult: Fix64; readonly reach2: Fix64 }

/** Whether a troop at (x, y) stands within the bark's reach of the flag cell (rx, ry). */
export function withinBark(bark: TroopBark, rx: number, ry: number, x: Fix64, y: Fix64): boolean {
  const dx = x.sub(Fix64.fromInt(rx)), dy = y.sub(Fix64.fromInt(ry));
  return dx.mul(dx).add(dy.mul(dy)).le(bark.reach2);
}

/**
 * The blow a troop takes (Pets v2/v3): × Rally Bark's and Shell Wall's cuts when it is a fortress troop within their reach
 * of the flag cell (rx, ry), then a Crystal Ward shield takes what it can. Without them the blow is untouched.
 */
export function troopBlow(t: Troop, blow: Fix64, bark: TroopBark | null, guard: TroopBark | null, rx: number, ry: number): Fix64 {
  if (t.ownerTowerId !== 0) return blow;
  let b = blow;
  if (bark !== null && withinBark(bark, rx, ry, t.x, t.y)) b = b.mul(bark.takenMult);
  if (guard !== null && withinBark(guard, rx, ry, t.x, t.y)) b = b.mul(guard.takenMult);
  if (t.shieldTicks > 0 && t.shield.isPositive()) { const soak = Fix64.min(t.shield, b); t.shield = t.shield.sub(soak); b = b.sub(soak); }
  return b;
}

/** Where slot `slot` of `slotCount` stands, in formation units: `depth` rows toward the enemy, `lateral` half-spacings across. */
export interface FormationSlot { readonly depth: number; readonly lateral: number }

/**
 * The troops' formation (DESIGN §7): a block `rows` deep and ceil(slots / rows) wide, across the road at the flag.
 * Slot 0 is the front row's centre; a row fills from the centre outwards, then the next row back. The front row is one
 * spacing toward the enemy, the last one spacing behind the flag (3 rows: +1, 0, −1). `lateral` is in half-spacings so
 * an even-width row sits symmetrically about the road's centre line with integer maths only.
 */
export function formationSlot(slot: number, slotCount: number, rows: number): FormationSlot {
  const r = Math.max(1, rows);
  const cols = Math.max(1, Math.ceil(slotCount / r));
  const row = Math.floor(slot / cols), k = slot % cols;
  // centre-out: 0, then the next to its right and left alternately (even widths start on the right of centre)
  const half = Math.floor(cols / 2);
  const col = cols % 2 === 1 ? half + (k % 2 === 1 ? (k + 1) / 2 : -k / 2) : (k % 2 === 0 ? half + k / 2 : half - (k + 1) / 2);
  return { depth: Math.floor((r - 1) / 2) - row, lateral: 2 * col - (cols - 1) };
}
